import { queryOptions, useMutation } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import {
  NewSemenRequest,
  ODSemenResponse,
  PulledSemenResponse,
  PullODSemenRequest,
  PullSemenRequest,
  RejectDiscardSemenRequest,
  ReturnSemenRequest,
  ScheduledDonorResponse,
  ScheduledSemenResponse,
  SemenMutationResult,
  SslQcData,
  SslQcMutationResult,
  SslQcRequest,
  UpdateDSSuggestedRequest,
  UpdateSemenRequest,
} from './types';

export type {
  NewSemenRequest as NewSemenInput,
  ODSemenResponse as ODSemenItem,
  PullODSemenRequest,
  PullSemenRequest,
  PulledSemenResponse as PulledSemenItem,
  RejectDiscardSemenRequest,
  ReturnSemenRequest,
  ScheduledDonorResponse as ScheduledDonorItem,
  ScheduledSemenResponse as ScheduledSemenItem,
  SemenMutationResult,
  UpdateDSSuggestedRequest,
  UpdateSemenRequest as UpdateSemenInput,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(path: string, params?: URLSearchParams): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/semen-pull/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/semen-pull/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  path: string,
  method: 'POST' | 'PUT',
  body: unknown,
): Promise<T> {
  const url = `${getBaseUrl()}/api/v1/semen-pull/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchScheduledSemen(params: {
  date: string;
  fertilizationSiteId: string;
}): Promise<ScheduledSemenResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  return fetchGet<ScheduledSemenResponse[]>('scheduled', searchParams);
}

async function fetchPulledSemen(params: {
  date: string;
  fertilizationSiteId: string;
}): Promise<PulledSemenResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  return fetchGet<PulledSemenResponse[]>('pulled', searchParams);
}

async function fetchScheduledDonors(params: {
  fertilizationSiteId: string;
  donorScheduleDate: string;
  sireId: string;
  semenId?: string | null;
  accountId?: string | null;
  semenType?: string | null;
  dsSemenLocId?: string | null;
}): Promise<ScheduledDonorResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  searchParams.set('donorScheduleDate', params.donorScheduleDate);
  searchParams.set('sireId', params.sireId);
  if (params.semenId) searchParams.set('semenId', params.semenId);
  if (params.accountId) searchParams.set('accountId', params.accountId);
  if (params.semenType) searchParams.set('semenType', params.semenType);
  if (params.dsSemenLocId)
    searchParams.set('dsSemenLocId', params.dsSemenLocId);
  return fetchGet<ScheduledDonorResponse[]>('scheduled-donors', searchParams);
}

async function fetchUnfinishedScheduledDonors(params: {
  donorScheduleDate: string;
  fertSiteId: string;
}): Promise<ScheduledDonorResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleDate', params.donorScheduleDate);
  searchParams.set('fertSiteId', params.fertSiteId);
  return fetchGet<ScheduledDonorResponse[]>(
    'unfinished-scheduled-donors',
    searchParams,
  );
}

async function fetchODSemen(params: {
  fertSiteId: string;
  semenName?: string;
  accountName?: string;
  caneCode?: string;
  colCode?: string;
}): Promise<ODSemenResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertSiteId', params.fertSiteId);
  if (params.semenName) searchParams.set('semenName', params.semenName);
  if (params.accountName) searchParams.set('accountName', params.accountName);
  if (params.caneCode) searchParams.set('caneCode', params.caneCode);
  if (params.colCode) searchParams.set('colCode', params.colCode);
  return fetchGet<ODSemenResponse[]>('od-semen', searchParams);
}

async function fetchODSimilarSemen(params: {
  fertSiteId: string;
  semenName?: string;
  accountName?: string;
  caneCode?: string;
  colCode?: string;
}): Promise<ODSemenResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertSiteId', params.fertSiteId);
  if (params.semenName) searchParams.set('semenName', params.semenName);
  if (params.accountName) searchParams.set('accountName', params.accountName);
  if (params.caneCode) searchParams.set('caneCode', params.caneCode);
  if (params.colCode) searchParams.set('colCode', params.colCode);
  return fetchGet<ODSemenResponse[]>('od-similar-semen', searchParams);
}

export const scheduledSemenOptions = (params: {
  date: string;
  fertilizationSiteId: string;
}) =>
  queryOptions({
    queryKey: ['semen-pull', 'scheduled', params],
    async queryFn() {
      return fetchScheduledSemen(params);
    },
  });

export function scheduledSemenV2Options(params: {
  date: string;
  fertilizationSiteId: string;
}) {
  return queryOptions({
    queryKey: ['semen-pull', 'scheduled', 'v2', params],
    async queryFn() {
      const searchParams = new URLSearchParams();
      searchParams.set('date', params.date);
      searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
      const url = `${getBaseUrl()}/api/v2/semen-pull/scheduled?${searchParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as ScheduledSemenResponse[];
    },
  });
}

export const pulledSemenOptions = (params: {
  date: string;
  fertilizationSiteId: string;
}) =>
  queryOptions({
    queryKey: ['semen-pull', 'pulled', params],
    async queryFn() {
      return fetchPulledSemen(params);
    },
  });

export const scheduledDonorsOptions = (params: {
  fertilizationSiteId: string;
  donorScheduleDate: string;
  sireId: string;
  semenId?: string | null;
  accountId?: string | null;
  semenType?: string | null;
  dsSemenLocId?: string | null;
}) =>
  queryOptions({
    queryKey: ['semen-pull', 'scheduled-donors', params],
    async queryFn() {
      return fetchScheduledDonors(params);
    },
  });

export const unfinishedScheduledDonorsOptions = (params: {
  donorScheduleDate: string;
  fertSiteId: string;
}) =>
  queryOptions({
    queryKey: ['semen-pull', 'unfinished-scheduled-donors', params],
    async queryFn() {
      return fetchUnfinishedScheduledDonors(params);
    },
  });

export const odSemenOptions = (params: {
  fertSiteId: string;
  semenName?: string;
  accountName?: string;
  caneCode?: string;
  colCode?: string;
}) =>
  queryOptions({
    queryKey: ['semen-pull', 'od-semen', params],
    async queryFn() {
      return fetchODSemen(params);
    },
  });

export const odSimilarSemenOptions = (params: {
  fertSiteId: string;
  semenName?: string;
  accountName?: string;
  caneCode?: string;
  colCode?: string;
}) =>
  queryOptions({
    queryKey: ['semen-pull', 'od-similar-semen', params],
    async queryFn() {
      return fetchODSimilarSemen(params);
    },
  });

async function fetchSslQcData(params: {
  date: string;
  siteId: string;
}): Promise<SslQcData | null> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  return fetchGet<SslQcData | null>('ssl-qc', searchParams);
}

async function saveSslQcData(
  request: SslQcRequest,
): Promise<SslQcMutationResult> {
  return fetchMutation<SslQcMutationResult>('ssl-qc', 'PUT', request);
}

export const sslQcOptions = (params: { date: string; siteId: string }) =>
  queryOptions({
    queryKey: ['semen-pull', 'ssl-qc', params],
    async queryFn() {
      return fetchSslQcData(params);
    },
  });

export function useSaveSslQcMutation() {
  return useMutation({
    mutationFn: (request: SslQcRequest) => saveSslQcData(request),
  });
}

async function pullSemen(
  request: PullSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>('pull', 'POST', request);
}

async function returnSemen(
  request: ReturnSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>('return', 'POST', request);
}

async function rejectDiscardSemen(
  request: RejectDiscardSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>('reject-discard', 'POST', request);
}

async function createNewSemenFn(
  newSemen: NewSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>('', 'POST', newSemen);
}

async function updateSemenFn(
  updateSemenData: UpdateSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>('', 'PUT', updateSemenData);
}

async function updateDSSuggestedFn(
  request: UpdateDSSuggestedRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>('ds-suggested', 'PUT', request);
}

async function pullODSemenFn(
  request: PullODSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>('pull-od', 'POST', request);
}

// React Query mutation hooks
export function useCreateNewSemenMutation() {
  return useMutation({
    mutationFn: (newSemen: NewSemenRequest) => createNewSemenFn(newSemen),
  });
}

export function useUpdateSemenMutation() {
  return useMutation({
    mutationFn: (updateSemenData: UpdateSemenRequest) =>
      updateSemenFn(updateSemenData),
  });
}

export function usePullSemenMutation() {
  return useMutation({
    mutationFn: (request: PullSemenRequest) => pullSemen(request),
  });
}

export function useReturnSemenMutation() {
  return useMutation({
    mutationFn: (request: ReturnSemenRequest) => returnSemen(request),
  });
}

export function useRejectDiscardSemenMutation() {
  return useMutation({
    mutationFn: (request: RejectDiscardSemenRequest) =>
      rejectDiscardSemen(request),
  });
}

export function useUpdateDSSuggestedMutation() {
  return useMutation({
    mutationFn: (request: UpdateDSSuggestedRequest) =>
      updateDSSuggestedFn(request),
  });
}

export function usePullODSemenMutation() {
  return useMutation({
    mutationFn: (request: PullODSemenRequest) => pullODSemenFn(request),
  });
}
